import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PhoneIcon } from "@heroicons/react/24/outline";
import { api, ApiError } from "@/lib/apiClient";
import { getRealtimeSessionId } from "@/lib/realtimeClient";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { IncomingCallDetails } from "@/features/incomingCall/IncomingCallDetails";
import {
  readCallerPhone,
  type IncomingCallNotice,
} from "@/features/incomingCall/incomingCall";

type PageState =
  | { status: "missing" }
  | { status: "loading" }
  | { status: "ready"; notice: IncomingCallNotice }
  | { status: "error"; message: string };

export function IncomingCallPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<PageState>({ status: "loading" });

  useEffect(() => {
    const phone = readCallerPhone(window.location.search);
    if (!phone) {
      setState({ status: "missing" });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    void (async () => {
      try {
        const notice = await api.post<IncomingCallNotice>("/kontakte/eingehender-anruf", {
          phone,
          source_session_id: getRealtimeSessionId(),
        });
        if (cancelled) return;
        setState({ status: "ready", notice });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.message
            : "Der Anruf konnte nicht an die App gemeldet werden.";
        setState({ status: "error", message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-grouped px-4 py-10 text-label">
      <div className="w-full max-w-md rounded-ios-lg bg-card p-6 shadow-ios-md">
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-accent">
            <PhoneIcon className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <h1 className="text-base font-semibold">Eingehender Anruf</h1>
            {state.status === "ready" ? (
              <p className="text-sm text-label-secondary">{state.notice.phone_display}</p>
            ) : null}
          </div>
        </div>

        {state.status === "loading" ? (
          <div className="flex items-center gap-2 text-sm text-label-secondary">
            <Spinner />
            <span>Kontakt wird gesucht …</span>
          </div>
        ) : null}

        {state.status === "missing" ? (
          <Alert variant="warning" title="Keine Rufnummer">
            Teams hat keine Nummer übergeben.
          </Alert>
        ) : null}

        {state.status === "error" ? (
          <Alert variant="error" title="Anruf nicht gemeldet">
            {state.message}
          </Alert>
        ) : null}

        {state.status === "ready" ? (
          <div className="space-y-4">
            <IncomingCallDetails
              matches={state.notice.matches}
              onOpen={(path) => navigate(path)}
            />
            <p className="text-sm text-label-secondary">
              In bereits geöffneten Fenstern erscheint der Anrufer ebenfalls. Dieses Fenster
              kann geschlossen werden.
            </p>
            <div className="flex justify-end">
              <Button type="button" variant="secondary" onClick={() => window.close()}>
                Fenster schließen
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
